/**
 * Hindi.
 *
 * Devanagari carries marks above and below the baseline, so this locale is the
 * one that catches a line-height tuned on Latin or Hebrew — the clipping the
 * brief warns about in §1.2 shows up here first.
 *
 * India is named in §7.5 as a market where in-chat commerce is the core
 * business use case, so this is not a courtesy locale either.
 *
 * Needs review by a native speaker before shipping; see the note in `ar.ts`.
 */
import type { MessageKey } from './en.ts';

export const hi: Partial<Record<MessageKey, string>> = {
  'app.opening': 'तिजोरी खोली जा रही है…',
  'app.empty.title': 'कोई बातचीत चुनें',
  'app.empty.body':
    'आपके संदेश एंड-टू-एंड एन्क्रिप्टेड हैं। सर्वर आप दोनों के बीच सिर्फ़ बंद लिफ़ाफ़े पहुँचाता है — न वह उन्हें खोल सकता है, न ही कोई और जिसे उस तक पहुँच मिल जाए।',
  'app.identityAlert.title': 'पहचान कुंजी बदल गई',
  'app.identityAlert.body':
    '{name} की पहचान कुंजी बदल गई है। हो सकता है उन्होंने ऐप दोबारा इंस्टॉल किया हो — या कोई उनका रूप धरने की कोशिश कर रहा हो।',
  'app.identityAlert.accept': 'नई कुंजी स्वीकार करें',
  'app.identityAlert.dismiss': 'अनदेखा करें',

  'onboarding.invited': '{name} ने आपको आमंत्रित किया है।',
  'onboarding.invited.detail': 'साइन अप करने के बाद उनके साथ आपकी बातचीत तैयार मिलेगी।',
  'onboarding.intro':
    'एंड-टू-एंड एन्क्रिप्टेड संदेश। आपकी कुंजियाँ यहीं, आपके डिवाइस पर बनती हैं, और कभी उसे नहीं छोड़तीं।',
  'onboarding.username.label': 'उपयोगकर्ता नाम',
  'onboarding.username.hint': '3–32 वर्ण, एक ही लिपि में।',
  'onboarding.displayName.label': 'प्रदर्शित नाम',
  'onboarding.phone.label': 'फ़ोन नंबर — वैकल्पिक',
  'onboarding.phone.hint':
    'सिर्फ़ इसलिए कि जिनके पास पहले से आपका नंबर है वे आपको ढूँढ़ सकें। यह आपके खाते का नाम नहीं है, इससे साइन इन नहीं होता, और बाद में इसे हटाने पर और कुछ नहीं जाता।',
  'onboarding.submit': 'खाता बनाएँ',
  'onboarding.submit.busy': 'कुंजियाँ बनाई जा रही हैं…',
  'onboarding.warning':
    'न कोई पासवर्ड है, न रिकवरी। आपकी पहचान एक निजी कुंजी है जो इसी ब्राउज़र में रखी है — साइट डेटा मिटाने पर आपको दोबारा साइन अप करना होगा।',
  'onboarding.username.error.too_short': 'कम से कम 3 वर्ण।',
  'onboarding.username.error.too_long': 'अधिकतम 32 वर्ण।',
  'onboarding.username.error.bad_start': 'बिंदु, हाइफ़न या अंडरस्कोर से शुरू नहीं हो सकता।',
  'onboarding.username.error.disallowed_character': 'इसमें एक वर्ण है जिसकी अनुमति नहीं है।',
  'onboarding.username.error.mixed_script':
    'इसमें दो लिपियाँ मिली हुई हैं। एक चुनें, ताकि कोई आपके नाम की नक़ल न कर सके।',
  'onboarding.username.error.reserved': 'यह नाम आरक्षित है।',

  'status.online': 'ऑनलाइन',
  'status.connecting': 'कनेक्ट हो रहा है…',
  'status.offline': 'ऑफ़लाइन',
  'sidebar.invite': 'दोस्तों को आमंत्रित करें',
  'sidebar.findByPhone': 'फ़ोन नंबर से खोजें',
  'sidebar.newChat': 'नई बातचीत',
  'sidebar.search': 'बातचीत खोजें',
  'sidebar.conversations': 'बातचीत',
  'sidebar.empty': 'अभी कोई बातचीत नहीं है।',
  'sidebar.noMatches': 'कोई मेल नहीं',
  'sidebar.noMessages': 'अभी कोई संदेश नहीं',
  'sidebar.group': 'समूह',
  'sidebar.notifications.on': 'सूचनाएँ चालू',
  'sidebar.notifications.denied': 'ब्राउज़र ने सूचनाएँ रोक दी हैं',
  'sidebar.notifications.enable': 'सूचनाएँ चालू करें',

  'thread.back': 'बातचीत सूची पर लौटें',
  'thread.typing': '{count, plural, one {{names} लिख रहे हैं…} other {{names} लिख रहे हैं…}}',
  'thread.members': '{count, plural, one {# सदस्य} other {# सदस्य}}',
  'thread.verifySafety': 'सुरक्षा नंबर सत्यापित करें',
  'thread.encrypted':
    'इस बातचीत के संदेश एंड-टू-एंड एन्क्रिप्टेड हैं। सर्वर भी उन्हें नहीं पढ़ सकता।',
  'thread.replyingTo': 'इसका उत्तर: {preview}',
  'thread.attachment': 'अनुलग्नक',
  'thread.cancelReply': 'उत्तर रद्द करें',
  'thread.recording': 'रिकॉर्ड हो रहा है… भेजने के लिए छोड़ें',
  'thread.cancelRecording': 'रिकॉर्डिंग रद्द करें',
  'thread.composer': 'संदेश',
  'thread.send': 'भेजें',
  'thread.voice.unavailable.title':
    'वॉइस रिकॉर्डिंग के लिए HTTPS चाहिए — इस पते पर ब्राउज़र माइक्रोफ़ोन रोक देता है',
  'thread.voice.unavailable.label': 'HTTPS के बिना वॉइस रिकॉर्डिंग उपलब्ध नहीं',
  'thread.voice.start': 'वॉइस संदेश रिकॉर्ड करें',
  'thread.voice.stop': 'रोकें और भेजें',
  'thread.voice.error.title': 'रिकॉर्ड नहीं हो सका',

  'bubble.deleted': 'यह संदेश हटा दिया गया',
  'bubble.actions': 'संदेश क्रियाएँ',
  'bubble.reply': 'उत्तर',
  'profile.profiles': 'प्रोफ़ाइल',
  'profile.profiles.explain': 'इस डिवाइस पर अलग-अलग खाते — काम, निजी, कोई व्यवसाय। सभी जुड़े रहते हैं, इसलिए किसी को भी संदेश आने पर सूचना मिलती है।',
  'profile.profiles.add': 'प्रोफ़ाइल जोड़ें',
  'profile.profiles.create': 'बनाएँ',
  'profile.profiles.namePlaceholder': 'काम, निजी…',
  'profile.profiles.unnamed': 'बिना नाम की प्रोफ़ाइल',
  'profile.profiles.remove': 'प्रोफ़ाइल हटाएँ',
  'profile.profiles.removeConfirm': 'यह खाता हमेशा के लिए हटाएँ?',
  'profile.profiles.warning': 'नई प्रोफ़ाइल अपने उपयोगकर्ता नाम वाला नया खाता है — इसी का दूसरा दृश्य नहीं। इसे भी पुनर्प्राप्त नहीं किया जा सकता।',
  'profile.build': 'बिल्ड {build}',
  'profile.name.edit': 'नाम बदलें',
  'profile.name.save': 'सहेजें',
  'profile.name.cancel': 'रद्द करें',
  'profile.name.failed': 'नाम नहीं बदला जा सका। कनेक्शन जाँचें और फिर से आज़माएँ।',
  'profile.delete': 'खाता हटाएँ',
  'profile.delete.explain': 'यह आपका खाता सर्वर से हमेशा के लिए हटा देता है। आपकी कुंजियाँ केवल यहीं हैं, इसलिए वापसी या पुनर्प्राप्ति संभव नहीं।',
  'profile.delete.others': 'आपके भेजे संदेश उन डिवाइसों पर बने रहते हैं जिन्हें वे मिले। किसी को बताया नहीं जाता — वे तब जानेंगे जब आपको लिखेंगे।',
  'profile.delete.confirmPrompt': 'पुष्टि के लिए अपना उपयोगकर्ता नाम लिखें',
  'profile.delete.confirm': 'हमेशा के लिए हटाएँ',
  'profile.delete.failed': 'खाता नहीं हटाया जा सका। कनेक्शन जाँचें और फिर से आज़माएँ।',
  'thread.recipientGone': 'यह व्यक्ति अब VAV पर नहीं है। आपके संदेश उन तक नहीं पहुँचेंगे।',
  'thread.recipientGone.title': 'यह खाता हटा दिया गया',
  'profile.avatar.change': 'फ़ोटो बदलें',
  'profile.avatar.remove': 'फ़ोटो हटाएँ',
  'profile.avatar.explain': 'आपकी फ़ोटो एन्क्रिप्टेड है और केवल उन लोगों को भेजी जाती है जिनसे आप बात करते हैं। अजनबी आपके आद्याक्षर देखते हैं।',
  'avatar.crop.title': 'फ़ोटो समायोजित करें',
  'avatar.crop.hint': 'स्थिति के लिए खींचें, ज़ूम के लिए स्लाइड करें। वृत्त के अंदर जो है वही सबको दिखेगा।',
  'avatar.crop.zoom': 'ज़ूम',
  'avatar.crop.loadFailed': 'यह फ़ोटो पढ़ी नहीं जा सकी। हो सकता है यह ऐसा फ़ॉर्मैट हो जिसे ऐप सपोर्ट नहीं करता — कोई और फ़ोटो आज़माएँ।',
  'call.incoming.audio': 'इनकमिंग वॉयस कॉल',
  'call.incoming.video': 'इनकमिंग वीडियो कॉल',
  'call.accept': 'उत्तर दें',
  'call.decline': 'अस्वीकार करें',
  'call.outgoing': 'घंटी बज रही है…',
  'call.cancel': 'रद्द करें',
  'call.hangup': 'कॉल समाप्त करें',
  'call.mute': 'म्यूट',
  'call.unmute': 'अनम्यूट',
  'call.cameraOff': 'कैमरा बंद',
  'call.cameraOn': 'कैमरा चालू',
  'call.connecting': 'कनेक्ट हो रहा है…',
  'call.ended.declined': 'कॉल अस्वीकार की गई',
  'call.ended.busy': 'व्यस्त',
  'call.ended.hangup': 'कॉल समाप्त',
  'call.ended.failed': 'कॉल शुरू नहीं हो सकी',
  'call.ended.unanswered': 'कोई उत्तर नहीं',
  'thread.call.audio': 'वॉयस कॉल',
  'thread.call.video': 'वीडियो कॉल',
  'call.legFailed': 'कनेक्ट नहीं हो सका',
  'call.turnMissing': 'रिले सर्वर (TURN) अभी सेट नहीं है — अलग नेटवर्क के बीच कॉल के लिए यह ज़रूरी है',
  'call.forProfile': 'आपकी प्रोफ़ाइल {name} के लिए',
  'call.toSpeaker': 'स्पीकर',
  'call.toEarpiece': 'ईयरपीस',
  'onboarding.link.start': 'मेरा खाता पहले से है — इस डिवाइस को लिंक करें',
  'onboarding.link.title': 'इस डिवाइस को लिंक करें',
  'onboarding.link.hint': 'जिस डिवाइस पर आपका खाता पहले से है: मेरी जानकारी, नया डिवाइस लिंक करें, और यह कोड स्कैन करें या पेस्ट करें।',
  'onboarding.link.verifyHint': 'देख लें कि ये अंक अनुमोदित करने वाले डिवाइस पर भी दिखें।',
  'onboarding.link.copy': 'लिंकिंग कोड कॉपी करें',
  'onboarding.link.copied': 'कॉपी हो गया ✓',
  'onboarding.link.waiting': 'अनुमोदन की प्रतीक्षा…',
  'onboarding.link.failed': 'लिंकिंग पूरी नहीं हुई। दोनों डिवाइस पर फिर से शुरू करें।',
  'profile.device.remove': 'डिस्कनेक्ट',
  'profile.logout': 'इस डिवाइस से लॉग आउट करें',
  'profile.logout.explain': 'आपका खाता आपके अन्य डिवाइस पर बना रहेगा। इस डिवाइस पर सहेजा गया सब कुछ इससे मिट जाएगा।',
  'profile.logout.confirm': 'लॉग आउट',
  'link.approve.stranded': 'इस ब्राउज़र में आपका खाता नहीं है, इसलिए यह नए डिवाइस को अनुमोदित नहीं कर सकता। लिंक को (या कोड पेस्ट करके) उस डिवाइस के VAV ऐप में खोलें जहाँ आपका खाता है।',
  'link.approve.title': 'नया डिवाइस अनुमोदित करें',
  'link.approve.account': 'नया डिवाइस {name} खाते से जुड़ जाएगा।',
  'profile.link.start': 'नया डिवाइस लिंक करें',
  'profile.link.hint': 'नए डिवाइस पर दिख रहा लिंकिंग कोड पेस्ट करें।',
  'profile.link.verifyHint': 'तभी अनुमोदित करें जब नए डिवाइस पर वही अंक दिखें।',
  'profile.link.approve': 'डिवाइस अनुमोदित करें',
  'profile.link.done': 'डिवाइस लिंक हो गया',
  'profile.link.failed': 'अनुमोदन नहीं हो सका — कोड की अवधि समाप्त हो सकती है।',
  'thread.date.today': 'आज',
  'thread.date.yesterday': 'कल',
  'contact.delete': 'संपर्क हटाएँ',
  'contact.delete.explain': 'बातचीत और उसका इतिहास केवल इस डिवाइस से हटेगा। उन्हें सूचित नहीं किया जाएगा, और उनकी प्रति उनके पास रहेगी।',
  'group.leave': 'समूह छोड़ें',
  'group.leave.explain': 'सदस्य देखेंगे कि आपने छोड़ दिया। समूह और उसका इतिहास इस डिवाइस से हट जाएगा।',
  'contact.title': 'संपर्क विवरण',
  'contact.verified': 'सत्यापित',
  'profile.title': 'मेरा विवरण',
  'profile.username': 'उपयोगकर्ता नाम',
  'profile.thisDevice': 'यह डिवाइस',
  'profile.devices': 'डिवाइस',
  'profile.devicesLoading': 'लोड हो रहा है…',
  'profile.devicesNone': 'सर्वर तक नहीं पहुँच सके।',
  'profile.deviceCurrent': 'यही',
  'profile.install': 'इस डिवाइस पर इंस्टॉल करें',
  'profile.install.action': 'होम स्क्रीन पर जोड़ें',
  'profile.install.already': 'आप इंस्टॉल किया गया ऐप उपयोग कर रहे हैं।',
  'profile.install.ios': 'शेयर मेनू खोलें और “होम स्क्रीन में जोड़ें” चुनें।',
  'profile.install.unavailable': 'आपका ब्राउज़र अभी इंस्टॉल का विकल्प नहीं दे रहा — आमतौर पर क्योंकि ऐप पहले से इंस्टॉल है। अपनी ऐप सूची में VAV खोजें।',
  'profile.noRecovery': 'आपकी कुंजियाँ केवल इसी डिवाइस पर हैं। ब्राउज़र डेटा हटाने या फ़ोन खोने से खाता चला जाता है — कोई रिकवरी नहीं है।',
  'bubble.forward': 'अग्रेषित करें',
  'bubble.select': 'चुनें',
  'bubble.moreReactions': 'और प्रतिक्रियाएँ',
  'select.cancel': 'चयन रद्द करें',
  'select.count': '{count, plural, =0 {संदेश चुनें} one {# चुना गया} other {# चुने गए}}',
  'forward.title': 'इन्हें अग्रेषित करें',
  'forward.send': '{to, plural, one {# चैट} other {# चैट}} में {count, plural, one {# संदेश} other {# संदेश}} भेजें',
  'thread.voice.blocked.how': 'Chrome ब्राउज़र में vav.chat खोलें (इंस्टॉल किए गए ऐप में नहीं), पता बार के बाईं ओर वाले आइकन पर टैप करें → अनुमतियाँ → अनुमतियाँ रीसेट करें। फिर माइक्रोफ़ोन दबाकर अनुमति दें।',
  'thread.voice.retry': 'फिर से आज़माएँ',
  'thread.voice.blockedByOs': 'इस साइट को माइक्रोफ़ोन की अनुमति है, इसलिए रुकावट यहाँ नहीं है — ब्राउज़र को ही फ़ोन से माइक्रोफ़ोन की अनुमति नहीं है।',
  'thread.voice.blockedByOs.how': 'Android: सेटिंग्स → ऐप्स → Chrome (या VAV, यदि आप इंस्टॉल किया ऐप उपयोग करते हैं) → अनुमतियाँ → माइक्रोफ़ोन → अनुमति दें।',
  'thread.voice.sendFailed': 'रिकॉर्डिंग नहीं भेजी जा सकी। कनेक्शन जाँचें और फिर से आज़माएँ।',
  'thread.voice.busy': 'माइक्रोफ़ोन की अनुमति है पर वह खुल नहीं सका। संभवतः कोई अन्य ऐप उसका उपयोग कर रहा है।',
  'thread.voice.busy.how': 'जो कुछ भी उसे रोके हो — कॉल, वॉइस असिस्टेंट, रिकॉर्डर — बंद करें और फिर आज़माएँ। फ़ोन रीस्टार्ट करने से अटका हुआ माइक छूट जाता है।',
  'thread.unreadFrom': '{count, plural, one {# नया संदेश} other {# नए संदेश}}',
  'thread.voice.blocked': 'इस साइट के लिए माइक्रोफ़ोन अवरुद्ध है। अस्वीकृत अनुमति दोबारा नहीं माँगी जा सकती — इसे सेटिंग्स में बदलना होगा।',
  'thread.voice.noMicrophone': 'इस डिवाइस पर कोई माइक्रोफ़ोन नहीं मिला।',
  'push.prompt.title': 'सूचनाएँ बंद हैं',
  'push.prompt.body': 'इन्हें चालू करें और संदेश आने पर आपका फ़ोन बताएगा, ऐप बंद होने पर भी।',
  'push.prompt.denied': 'इस साइट को सूचनाएँ भेजने से रोका गया है। ब्राउज़र की साइट सेटिंग्स में अनुमति दें और लौटें।',
  'push.prompt.enable': 'चालू करें',
  'push.prompt.dismiss': 'अभी नहीं',
  'quote.you': 'आप',
  'quote.them': 'वे',
  'quote.unavailable': 'संदेश उपलब्ध नहीं',
  'quote.image': 'फ़ोटो',
  'quote.video': 'वीडियो',
  'quote.audio': 'ऑडियो',
  'quote.voice': 'वॉइस संदेश',
  'schedule.title': 'बाद में भेजें',
  'schedule.explain': 'संदेश अभी लिखा और सील किया जाता है, और आपके चुने समय पर जाता है। तब तक इसे ⋯ मेनू से रद्द किया जा सकता है।',
  'schedule.confirm': 'शेड्यूल करें',
  'bubble.cancelScheduled': 'शेड्यूल किया भेजना रद्द करें',
  'bubble.state.scheduled': 'शेड्यूल किया गया',
  'profile.checkUpdate': 'अपडेट जाँचें',
  'profile.checkUpdate.busy': 'जाँच रहा है…',
  'profile.checkUpdate.current': 'आप नवीनतम संस्करण पर हैं।',
  'bubble.edit': 'संपादित करें',
  'bubble.edited': 'संपादित',
  'thread.editing': 'संदेश संपादित हो रहा है',
  'group.edit.title': 'समूह विवरण',
  'group.edit.name': 'समूह का नाम',
  'bubble.deleteForMe': 'मेरे लिए हटाएँ',
  'bubble.save': 'छवि सहेजें',
  'bubble.closeImage': 'छवि बंद करें',
  'bubble.deleteForEveryone': 'सबके लिए हटाएँ',
  'bubble.decryptFailed': 'यह अनुलग्नक डिक्रिप्ट नहीं हो सका।',
  'bubble.decrypting': 'डिक्रिप्ट हो रहा है…',
  'bubble.state.pending': 'लंबित',
  'bubble.state.sent': 'भेजा गया',
  'bubble.state.delivered': 'पहुँचा',
  'bubble.state.read': 'पढ़ा गया',
  'bubble.state.failed': 'विफल',

  'attach.label': 'संलग्न करें',
  'attach.camera': 'कैमरा',
  'attach.image': 'फ़ोटो',
  'attach.video': 'वीडियो',
  'attach.audio': 'ऑडियो',
  'attach.document': 'दस्तावेज़',
  'attach.error.title': 'संलग्न नहीं हो सका',

  'media.play': 'चलाएँ',
  'media.pause': 'रोकें',
  'media.seek': 'रिकॉर्डिंग में स्थिति',
  'media.decrypting': 'डिक्रिप्ट हो रहा है…',
  'media.permission.library': 'फ़ोटो लाइब्रेरी तक पहुँच की अनुमति नहीं',
  'media.permission.camera': 'कैमरे तक पहुँच की अनुमति नहीं',
  'media.permission.microphone': 'माइक्रोफ़ोन तक पहुँच की अनुमति नहीं',

  'invite.title': 'दोस्तों को आमंत्रित करें',
  'invite.close': 'बंद करें',
  'invite.body':
    'लिंक भेजें। जो इसे खोलेगा उसे दिखेगा कि आपने आमंत्रित किया है, और साइन अप करते ही आपकी बातचीत तैयार मिलेगी।',
  'invite.preparing': 'लिंक तैयार हो रहा है…',
  'invite.share': 'साझा करें',
  'invite.copy': 'लिंक कॉपी करें',
  'invite.copied': 'कॉपी हो गया',
  'invite.copyFailed': 'कॉपी नहीं हो सका — लिंक चुनकर खुद कॉपी करें',
  'invite.caution':
    'लिंक 30 दिन तक चलेगा और दोबारा इस्तेमाल हो सकता है। जिसके पास भी यह होगा वह साइन अप कर सकता है और देख सकता है कि आपने आमंत्रित किया — इसे सार्वजनिक जगह पर न डालें।',
  'invite.message': '{name} आपको VAV पर आमंत्रित कर रहे हैं — एंड-टू-एंड एन्क्रिप्टेड संदेश।',
  'invite.defaultName': 'एक दोस्त',

  'newChat.title': 'नई बातचीत',
  'newChat.search': 'उपयोगकर्ता नाम, पूरा नाम या फ़ोन नंबर',
  'newChat.searchByUsername': 'उपयोगकर्ता नाम से खोजें',
  'newChat.direct': 'बातचीत',
  'newChat.group': 'समूह',
  'newChat.groupName': 'समूह का नाम',
  'newChat.pickHint': 'परिणामों में से प्रतिभागी चुनें — जितने चाहें।',
  'newChat.searching': 'खोजा जा रहा है…',
  'newChat.noResults': 'इस नाम का कोई उपयोगकर्ता नहीं मिला।',
  'newChat.createGroup': 'समूह बनाएँ',
  'newChat.openChat': 'बातचीत खोलें',

  'phone.title': 'फ़ोन नंबर',
  'phone.privacy':
    'भेजने से पहले आपका नंबर आपके ही डिवाइस पर हैश हो जाता है। सर्वर सिर्फ़ छाप रखता है, नंबर कभी नहीं।',
  'phone.mine': 'मेरा नंबर — ताकि लोग मुझे ढूँढ सकें',
  'phone.save': 'सहेजें',
  'phone.saved': 'सहेजा गया',
  'phone.findFriends': 'दोस्त खोजें',
  'phone.fromContacts': 'संपर्कों में से चुनें',
  'phone.noContactPicker':
    'यह ब्राउज़र संपर्कों तक पहुँच नहीं देता। जाँचने के लिए नंबर टाइप करें, या Android पर Chrome इस्तेमाल करें।',
  'phone.checkOne': 'एक नंबर जाँचें',
  'phone.search': 'खोजें',
  'phone.noneRegistered': 'इनमें से कोई नंबर पंजीकृत नहीं है।',
  'phone.invalid': 'यह फ़ोन नंबर नहीं लगता',

  'safety.title': 'सुरक्षा नंबर',
  'safety.compare':
    'इस नंबर की तुलना {name} से किसी दूसरे रास्ते से करें — आमने-सामने या फ़ोन पर। अगर नंबर एक जैसे हैं, तो बीच में कोई नहीं है।',
  'safety.confirm': 'मैंने यह नंबर सत्यापित कर लिया',
  'safety.changeNote':
    'अगर {name} ऐप दोबारा इंस्टॉल करेंगे तो नंबर बदल जाएगा और आपको सूचना मिलेगी। अप्रत्याशित बदलाव को भेजना जारी रखने से पहले जाँच लेना ठीक रहता है।',
  'safety.noSession':
    '{name} के साथ अभी तक कोई कुंजी साझा नहीं हुई है। एक संदेश भेजें और यहाँ लौटें।',

  'common.cancel': 'रद्द करें',
  'push.channel': 'संदेश',
  'push.newMessage': 'नया संदेश',
  'language.title': 'भाषा',
  'language.note': 'आपके ब्राउज़र से पहचानी गई। अनुमान ग़लत हो तो यहाँ बदलें।',
  'language.restart': 'लेआउट दिशा का बदलाव पूरा करने के लिए ऐप दोबारा शुरू करें।',
  'app.failed.title': 'शुरू नहीं हो सका',
  'app.failed.retry': 'फिर कोशिश करें',
  'phone.invalidHint': 'पूरा अंतरराष्ट्रीय रूप आज़माएँ, अपने देश कोड से शुरू करके।',
  'phone.country': 'देश',
  'phone.national': 'आपका नंबर, जैसे आप स्थानीय रूप से डायल करते हैं',
  'phone.willSave': '{number} के रूप में सहेजा जाएगा',
  'phone.searchCountry': 'देश खोजें',
  'phone.importContacts': 'मेरे संपर्कों में खोजें',
  'phone.importing': 'आपके संपर्क जाँचे जा रहे हैं…',
  'phone.importingProgress': 'आपके संपर्क जाँचे जा रहे हैं… {total} में से {done}',
  'phone.importScanning': 'आपके संपर्क पढ़े जा रहे हैं…',
  'phone.importScanned': '{entries} प्रविष्टियाँ, {unique, plural, one {जाँचने के लिए # नंबर} other {जाँचने के लिए # नंबर}}',
  'phone.contactsDenied': 'संपर्क पढ़ने की अनुमति नहीं मिली। आप अब भी कोई नंबर खुद जाँच सकते हैं।',
  'phone.matched': '{count, plural, =0 {आपका कोई संपर्क अभी यहाँ नहीं है} one {# संपर्क यहाँ है} other {# संपर्क यहाँ हैं}}',
  'phone.contactsPrivacy': 'आपके संपर्क इसी डिवाइस पर हैश होते हैं। पता-पुस्तिका कभी अपलोड नहीं होती।',
  'invite.share.subject': 'VAV पर मेरे साथ जुड़ें',
};
